import { clsx } from "clsx";
import { useId, useState, type ReactNode } from "react";
import { Link } from "wouter";

export interface FilaTabla { celdas: ReactNode[] }
export interface TablaDatos { columnas: string[]; filas: FilaTabla[] }

/** Contenedor de una gráfica: título, lectura de una línea (determinista), gráfica y su tabla gemela (accesible e imprimible). */
export function Figura({ titulo, lectura, children, tabla, faltan, vacio, className, accion, id }: {
  titulo: string; lectura?: ReactNode; children?: ReactNode; tabla?: TablaDatos; faltan?: { texto: string; href?: string }[];
  vacio?: boolean; className?: string; accion?: ReactNode; id?: string;
}) {
  const [verTabla, setVerTabla] = useState(false);
  const uid = useId();
  return (
    <figure id={id} className={clsx("viz-figura rounded-md border border-borde bg-white p-4 scroll-mt-20", className)} aria-labelledby={`${uid}-t`}>
      <div className="flex items-start justify-between gap-3">
        <h2 id={`${uid}-t`} className="text-base font-semibold">{titulo}</h2>
        {accion}
      </div>
      {lectura && !vacio && <p className="mt-1 text-sm text-tinta-suave">{lectura}</p>}
      {vacio ? (
        <div className="mt-3 rounded-md border border-dashed border-borde bg-fondo p-4 text-sm text-tinta-suave">
          {faltan && faltan.length > 0 ? (
            <ul className="flex flex-col gap-1">{faltan.slice(0, 3).map((f, i) => <li key={i}>Por llenar: {f.href ? <Link href={f.href} className="font-medium text-primary underline">{f.texto}</Link> : <b className="text-tinta">{f.texto}</b>}</li>)}</ul>
          ) : "Todavía no hay datos para esta gráfica."}
        </div>
      ) : (
        <>
          <div className="mt-3" aria-hidden={tabla ? true : undefined}>{children}</div>
          {tabla && tabla.filas.length > 0 && (
            <div className="mt-2">
              <button type="button" onClick={() => setVerTabla((v) => !v)} aria-expanded={verTabla} className="text-xs font-medium text-tinta-suave underline-offset-2 hover:underline print:hidden">
                {verTabla ? "Ocultar tabla" : "Ver como tabla"}
              </button>
              <div className={clsx(verTabla ? "block" : "sr-only print:not-sr-only print:block")}>
                <table className="tabular mt-2 w-full text-left text-sm">
                  <thead><tr className="border-b border-borde text-xs text-tinta-suave">{tabla.columnas.map((c, i) => <th key={i} scope="col" className={clsx("py-1.5 font-medium", i > 0 && "text-right")}>{c}</th>)}</tr></thead>
                  <tbody>{tabla.filas.map((f, i) => <tr key={i} className="border-b border-borde/60 last:border-0">{f.celdas.map((c, j) => <td key={j} className={clsx("py-1.5", j > 0 && "text-right")}>{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </figure>
  );
}

/** Leyenda: solo cuando hay ≥2 series. Texto en tinta, el color va en la muestra. */
export function Leyenda({ items, className }: { items: { etiqueta: string; color: string; forma?: "rect" | "linea" | "anillo" | "punto" }[]; className?: string }) {
  if (items.length < 2) return null;
  return (
    <ul className={clsx("flex flex-wrap gap-x-4 gap-y-1 text-xs text-tinta-suave", className)}>
      {items.map((it) => (
        <li key={it.etiqueta} className="flex items-center gap-1.5">
          {it.forma === "linea" ? <span className="inline-block h-0.5 w-4 rounded" style={{ background: it.color }} />
            : it.forma === "anillo" ? <span className="inline-block h-2.5 w-2.5 rounded-full border-2 bg-white" style={{ borderColor: it.color }} />
            : it.forma === "punto" ? <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />
            : <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: it.color }} />}
          {it.etiqueta}
        </li>
      ))}
    </ul>
  );
}

/** Tooltip flotante dentro de un contenedor `relative`. */
export function Tooltip({ x, y, children, visible }: { x: number; y: number; children: ReactNode; visible: boolean }) {
  if (!visible) return null;
  return (
    <div role="status" className="viz-tooltip pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-borde bg-white px-2.5 py-1.5 text-xs text-tinta shadow-md"
      style={{ left: x, top: y - 8 }}>{children}</div>
  );
}
